import { apiErrorField } from '@agos/api-client'
import {
  getGetEmployeesQueryKey,
  useDeleteEmployeesId,
  useGetEmployees,
  usePatchEmployeesId,
  usePostEmployees,
} from '@agos/api-client/hr'
import { useGetHikvisionConfig } from '@agos/api-client/devices'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  PhoneInput,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Mail,
  Phone,
  Plus,
  Trash2,
  UserCheck2,
  UserX,
  Users,
  X,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  SYSTEM_ROLES,
  canSaveEmployee,
  deactivateRestriction,
  deleteRestriction,
  demotionLocksEveryoneOut,
  driftedPositions,
  emptyEmployeeForm,
  employeeFormOf,
  employeePayload,
  employeeProblems,
  filterRoster,
  initialsOf,
  isSystemRole,
  positionSuggestions,
  positionsThatAreRoles,
  restrictionText,
  roleAccess,
  roleColor,
  roleLabel,
  roleOptions,
  rosterTotals,
  type Employee,
  type EmployeeForm,
  type EmployeeProblem,
  type Restriction,
} from './staff-roster'

/**
 * `/employees` — who works here and what each of them can reach.
 *
 * **FB-099.** Legacy's role filter lists four of the five roles: no `cook`. There is one cook on the
 * staff, so that person cannot be filtered for and the chip counts do not add up — *Všetky* says 23
 * against 22 across the four. And `cook` is the form's **default**, so an employee added without
 * touching the dropdown lands on the role the filter cannot select. The options are built from the
 * roles the staff actually hold, and the default is the commonest non-administrator one.
 *
 * **FB-100.** `position` is free text and the data shows it: fourteen spellings for twelve jobs,
 * `Šofer` six times beside `Šofér` twice, `veduci` beside `Veduci`, and one employee whose position is
 * the literal string `waiter`. The field offers what is already in use, warns when a new spelling
 * means an existing one, and refuses a system role typed into it.
 *
 * Ported from legacy `pages/employees.tsx` (590 lines).
 */

interface HikvisionConfig {
  ip?: string | null
}

export function EmployeesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user } = useAuth()

  const { data, isLoading } = useGetEmployees()
  const employees = useMemo<Employee[]>(() => (data ?? []), [data])
  // The card field only makes sense once a terminal exists — legacy's own rule, `!!cfg?.ip`.
  const { data: terminalData } = useGetHikvisionConfig()
  const terminal = (terminalData ?? {}) as HikvisionConfig
  const terminalConfigured = (terminal.ip ?? '') !== ''

  const [tab, setTab] = useState<'active' | 'inactive'>('active')
  const [role, setRole] = useState('all')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<{ id: number | null; form: EmployeeForm } | null>(null)
  const [toggling, setToggling] = useState<Employee | null>(null)
  const [deleting, setDeleting] = useState<Employee | null>(null)

  const totals = rosterTotals(employees)
  const options = roleOptions(employees, tab)
  const shown = filterRoster(employees, { tab, role, search })
  const drifted = driftedPositions(employees)
  const roleTyped = positionsThatAreRoles(employees)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Zmena sa neuložila.'),
      variant: 'destructive',
    })

  const create = usePostEmployees({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        invalidate()
        toast({ title: tt('Zamestnanec pridaný') })
      },
      onError,
    },
  })
  const patch = usePatchEmployeesId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        setToggling(null)
        invalidate()
      },
      onError,
    },
  })
  const remove = useDeleteEmployeesId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })

  // A role the filter no longer offers would otherwise show an empty list with no explanation.
  const setTabAndKeepRole = (next: 'active' | 'inactive') => {
    setTab(next)
    const stillThere = roleOptions(employees, next).some((option) => option.value === role)
    if (!stillThere) setRole('all')
  }

  const columns: Column<Employee>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Meno'),
        sortable: true,
        sortValue: (employee) => employee.name ?? '',
        render: (employee) => (
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">
              {initialsOf(employee.name)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                {employee.name}
                {employee.id === user?.id && (
                  <span
                    className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full"
                    data-self
                  >
                    <T>Ja</T>
                  </span>
                )}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">{employee.position}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'role',
        header: tt('Rola'),
        sortable: true,
        sortValue: (employee) => roleLabel(employee.systemRole),
        render: (employee) => (
          <StatusBadge label={roleLabel(employee.systemRole)} color={roleColor(employee.systemRole)} />
        ),
      },
      {
        key: 'email',
        header: tt('E-mail'),
        sortable: true,
        sortValue: (employee) => employee.email ?? '',
        render: (employee) =>
          employee.email ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
              <Mail className="w-3 h-3 shrink-0" />
              <span className="truncate">{employee.email}</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
      },
      {
        key: 'phone',
        header: tt('Telefón'),
        render: (employee) =>
          employee.phone ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <Phone className="w-3 h-3 shrink-0" />
              {employee.phone}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (employee) => {
          const cannotToggle = deactivateRestriction(employee, employees)
          const cannotDelete = deleteRestriction(employee, employees, user?.id)
          return (
            <div className="flex items-center gap-1 justify-end">
              {/* Legacy hides the toggle on a main admin and explains only after the click. */}
              <IconButton
                label={`${employee.active === false ? tt('Aktivovať') : tt('Deaktivovať')} ${employee.name ?? ''}`}
                disabled={cannotToggle !== null}
                hint={cannotToggle === null ? undefined : restrictionText(cannotToggle)}
                onClick={() => setToggling(employee)}
              >
                {employee.active === false ? (
                  <UserCheck2 className="w-3.5 h-3.5" />
                ) : (
                  <UserX className="w-3.5 h-3.5" />
                )}
              </IconButton>
              <button
                type="button"
                onClick={() => setEditing({ id: employee.id ?? null, form: employeeFormOf(employee) })}
                aria-label={`${tt('Upraviť')} ${employee.name ?? ''}`}
                className="px-2 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
              >
                <T>Upraviť</T>
              </button>
              <IconButton
                label={`${tt('Zmazať')} ${employee.name ?? ''}`}
                tone="red"
                disabled={cannotDelete !== null}
                hint={cannotDelete === null ? undefined : restrictionText(cannotDelete)}
                onClick={() => setDeleting(employee)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </IconButton>
            </div>
          )
        },
      },
    ],
    [employees, user],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Zamestnanci')}
        description={`${formatCount(totals.active)} ${tt('aktívnych')} · ${formatCount(totals.inactive)} ${tt('neaktívnych')}`}
        icon={<Users className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => setEditing({ id: null, form: emptyEmployeeForm(employees) })}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" />
            <T>Pridať zamestnanca</T>
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-roster-totals>
        <StatCard label={tt('Zamestnancov')} value={formatCount(totals.total)} icon={Users} color="blue" />
        <StatCard label={tt('Aktívnych')} value={formatCount(totals.active)} icon={UserCheck2} color="emerald" />
        <StatCard
          label={tt('Neaktívnych')}
          value={formatCount(totals.inactive)}
          icon={UserX}
          color={totals.inactive > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Rôznych pozícií')}
          value={formatCount(positionSuggestions(employees).length)}
          icon={AlertCircle}
          color={drifted.length > 0 ? 'amber' : 'emerald'}
          sub={drifted.length > 0 ? `${drifted.length} ${tt('napísaných dvojako')}` : undefined}
        />
      </div>

      {/* FB-100 - the drift, named. */}
      {drifted.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-position-drift>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Rovnaká pozícia je napísaná dvojako:</T>{' '}
            {drifted.map((group) => group.spellings.join(' / ')).join(' · ')}.{' '}
            <T>Každý prehľad podľa pozície ich počíta ako dve rôzne.</T>
          </span>
        </p>
      )}
      {roleTyped.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-position-is-role>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>V poli pozícia je systémová rola:</T> {roleTyped.join(', ')}.{' '}
            <T>Pozícia je to, čo človek robí; rola je to, čo vidí v systéme.</T>
          </span>
        </p>
      )}

      <TabGroup
        variant="bar"
        tabs={[
          { id: 'active', label: `${tt('Aktívni')} (${totals.active})` },
          { id: 'inactive', label: `${tt('Neaktívni')} (${totals.inactive})` },
        ]}
        active={tab}
        onChange={(id) => setTabAndKeepRole(id as 'active' | 'inactive')}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať meno, e-mail, telefón alebo pozíciu...'),
        }}
        filters={
          <FilterSelect
            value={role}
            label={tt('Rola')}
            onChange={setRole}
            options={options.map((option) => ({
              value: option.value,
              label: `${option.label} (${option.count})`,
            }))}
          />
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-employees>
          <DataTable<Employee>
            columns={columns}
            data={shown}
            rowKey={(employee) => employee.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Nikto nezodpovedá filtru.')}
            emptyIcon={<Users className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {/* Legacy's legend, kept - it is the only place the roles' reach is written down. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-role-legend>
        {SYSTEM_ROLES.map((systemRole) => (
          <div key={systemRole} className="rounded-2xl border border-border bg-card p-4 space-y-1.5">
            <StatusBadge label={roleLabel(systemRole)} color={roleColor(systemRole)} />
            {roleAccess(systemRole).map((entry) => (
              <p key={entry} className="text-[11px] text-muted-foreground">
                · {entry}
              </p>
            ))}
          </div>
        ))}
      </div>

      {editing !== null && (
        <EmployeeDialog
          form={editing.form}
          editingId={editing.id}
          employees={employees}
          terminalConfigured={terminalConfigured}
          saving={create.isPending || patch.isPending}
          onChange={(next) => setEditing({ ...editing, form: next })}
          onClose={() => setEditing(null)}
          onSave={() => {
            const payload = employeePayload(editing.form)
            if (editing.id === null) create.mutate({ data: payload })
            else patch.mutate({ id: editing.id, data: payload })
          }}
        />
      )}

      <ConfirmDialog
        open={toggling !== null}
        onClose={() => setToggling(null)}
        onConfirm={() =>
          toggling?.id !== undefined &&
          patch.mutate({ id: toggling.id, data: { active: toggling.active === false } })
        }
        title={
          toggling?.active === false
            ? `${tt('Aktivovať')} ${toggling?.name ?? ''}?`
            : `${tt('Deaktivovať')} ${toggling?.name ?? ''}?`
        }
        description={
          toggling?.active === false
            ? tt('Zamestnanec sa bude môcť znova prihlásiť.')
            : tt('Zamestnanec sa nebude môcť prihlásiť. Jeho história objednávok zostáva.')
        }
        confirmLabel={toggling?.active === false ? tt('Aktivovať') : tt('Deaktivovať')}
        loading={patch.isPending}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ id: deleting.id })}
        title={`${tt('Zmazať')} ${deleting?.name ?? ''}?`}
        description={tt(
          'Zamestnanca to odstráni zo zoznamu. Deaktivácia je zvyčajne to, čo chcete - tá mu zoberie prístup a históriu ponechá.',
        )}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

// ─── the form ────────────────────────────────────────────────────────────────

function EmployeeDialog({
  form,
  editingId,
  employees,
  terminalConfigured,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  form: EmployeeForm
  editingId: number | null
  employees: Employee[]
  terminalConfigured: boolean
  saving: boolean
  onChange: (next: EmployeeForm) => void
  onClose: () => void
  onSave: () => void
}) {
  const problems = employeeProblems(form, employees, editingId)
  const existing = employees.find((employee) => employee.id === editingId)
  const locksOut =
    existing === undefined ? false : demotionLocksEveryoneOut(form, existing, employees)
  const suggestions = positionSuggestions(employees)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editingId === null ? tt('Nový zamestnanec') : tt('Upraviť zamestnanca')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-employee-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">
            {editingId === null ? tt('Nový zamestnanec') : `${tt('Upraviť')} ${form.name}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <Field label={tt('Meno')}>
            <input
              value={form.name}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              aria-label={tt('Meno')}
              className={INPUT}
            />
          </Field>

          <Field label={tt('Pozícia')}>
            <input
              value={form.position}
              onChange={(event) => onChange({ ...form, position: event.target.value })}
              aria-label={tt('Pozícia')}
              list="agos-positions"
              className={INPUT}
            />
            {/* FB-100 - the spellings already in use, so the next one matches. */}
            <datalist id="agos-positions" data-position-suggestions>
              {suggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
          </Field>

          <Field label={tt('Rola v systéme')}>
            <select
              value={form.systemRole}
              onChange={(event) =>
                isSystemRole(event.target.value) && onChange({ ...form, systemRole: event.target.value })
              }
              aria-label={tt('Rola v systéme')}
              className={INPUT}
            >
              {SYSTEM_ROLES.map((systemRole) => (
                <option key={systemRole} value={systemRole}>
                  {roleLabel(systemRole)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              {roleAccess(form.systemRole).join(' · ')}
            </p>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={tt('E-mail')}>
              <input
                value={form.email}
                onChange={(event) => onChange({ ...form, email: event.target.value })}
                aria-label={tt('E-mail')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Telefón')}>
              <PhoneInput
                value={form.phone}
                onChange={(next) => onChange({ ...form, phone: next })}
                label={tt('Telefón')}
                testId="employee"
              />
            </Field>
          </div>

          <Field label={editingId === null ? tt('Heslo') : tt('Nové heslo (nechajte prázdne)')}>
            <input
              type="password"
              value={form.password}
              onChange={(event) => onChange({ ...form, password: event.target.value })}
              aria-label={tt('Heslo')}
              className={INPUT}
            />
          </Field>

          {terminalConfigured && (
            <Field label={tt('Číslo karty (terminál)')}>
              <input
                value={form.hikvisionCardNo}
                onChange={(event) => onChange({ ...form, hikvisionCardNo: event.target.value })}
                aria-label={tt('Číslo karty')}
                className={INPUT}
              />
            </Field>
          )}

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.showWallet}
              onChange={(event) => onChange({ ...form, showWallet: event.target.checked })}
              aria-label={tt('Zobrazovať peňaženku')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Zobrazovať peňaženku - koľko hotovosti pri sebe nesie</T>
          </label>
          {/* §9.2 - only for a driver, because it decides nothing for anybody else: it is what makes
            * the per-delivery reward apply and what `/external-drivers` lists. Until now the column
            * had one writer, the retired driver app's self-registration, so no administrator could
            * create the external driver the whole feature is about. */}
          {form.systemRole === 'driver' && (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.isExternal}
                onChange={(event) => onChange({ ...form, isExternal: event.target.checked })}
                aria-label={tt('Externý vodič')}
                data-employee-external
                className="w-4 h-4 rounded accent-primary"
              />
              <T>Externý vodič - platený za rozvoz, nie hodinovo</T>
            </label>
          )}
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => onChange({ ...form, active: event.target.checked })}
              aria-label={tt('Aktívny')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Aktívny - môže sa prihlásiť</T>
          </label>

          {locksOut && (
            <p className="flex items-start gap-1.5 text-xs text-red-500" data-locks-out>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              {/* Nothing in legacy stops a main administrator demoting themselves. */}
              <T>
                Toto je posledný hlavný administrátor. Po zmene roly sa do nastavení nikto nedostane.
              </T>
            </p>
          )}

          {problems.length > 0 && (
            <div className="space-y-1" data-employee-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'position-duplicate' || problem.kind === 'no-credentials'
                      ? 'text-amber-500'
                      : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !canSaveEmployee(problems) || locksOut}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProblemText({ problem }: { problem: EmployeeProblem }) {
  if (problem.kind === 'no-name') return <T>Meno je povinné.</T>
  if (problem.kind === 'name-too-short') return <T>Meno má mať aspoň dva znaky.</T>
  if (problem.kind === 'no-position') return <T>Pozícia je povinná.</T>
  if (problem.kind === 'position-is-role') {
    return (
      <span>
        <strong>{problem.value}</strong>{' '}
        <T>je systémová rola, nie pozícia. Pozícia je to, čo človek robí.</T>
      </span>
    )
  }
  if (problem.kind === 'position-duplicate') {
    return (
      <span>
        <T>Táto pozícia sa už používa ako</T> <strong>{problem.existing}</strong> -{' '}
        <T>dve písania toho istého sa v prehľadoch počítajú zvlášť.</T>
      </span>
    )
  }
  if (problem.kind === 'bad-email') return <T>E-mail nemá platný tvar.</T>
  if (problem.kind === 'email-taken') {
    return (
      <span>
        <T>Tento e-mail už má</T> <strong>{problem.name}</strong>.
      </span>
    )
  }
  if (problem.kind === 'password-too-short') return <T>Heslo má mať aspoň šesť znakov.</T>
  return <T>Bez e-mailu a bez hesla sa tento človek nebude môcť prihlásiť.</T>
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}

/** Disabled with the reason in its title, rather than a toast after the click. */

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}

export type { Restriction }
